import React, { Component } from 'react'

import { isValid } from 'date-fns'
import {
  ArrowDownLeftIcon,
  ArrowUpRightIcon,
  CalendarIcon,
  Copy,
  Import,
  Info,
  Settings2Icon,
} from 'lucide-react'
import { makeObservable } from 'mobx'
import { observer } from 'mobx-react'

import { SharingMode } from '~/client/graph'
import { ExternalCalendarDateRange } from '~/client/src/desktop/enums/ExternalCalendarDateRange'
import { ExternalCalendarWorkflowType } from '~/client/src/desktop/enums/ExternalCalendarWorkflowType'
import MenuCloser from '~/client/src/shared/components/MenuCloser'
import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import { Separator } from '~/client/src/shared/components/shadcn-ui/separator'
import { Switch } from '~/client/src/shared/components/shadcn-ui/switch'
import Spinner from '~/client/src/shared/components/Spinner/Spinner'
import StruxhubInput from '~/client/src/shared/components/StruxhubInputs/StruxhubInput'
import TooltipWrapper from '~/client/src/shared/components/TooltipWrapper/TooltipWrapper'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import CalendarLinkInputStore from './CalendarLinkInput.store'
import CalendarDatePicker from './components/CalendarDatePicker'
import CalendarOptionsPicker from './components/CalendarOptionsPicker'
import CalendarSubscriptions from './components/CalendarSubscriptions'

import 'react-day-picker/style.css'
import './CalendarInputLink.styles.scss'

interface IProps {
  store: CalendarLinkInputStore
}

const INPUT_DELAY = 300

@observer
export default class CalendarLinkInput extends Component<IProps> {
  public constructor(props: IProps) {
    super(props)

    makeObservable(this)
  }

  public componentDidMount() {
    this.store.initialize()
  }

  public render(): JSX.Element {
    return (
      <>
        {this.store.isDisableAccessDialogShown && this.renderCloseDialog()}
        {this.store.isRemoveSubscriptionDialogShown &&
          this.renderRemoveSubscriptionDialog()}
        <div className="row y-center setup-form-parameter text bold">
          <div className="row y-start">
            <div className="col ba-light-input-border pa10 brada10 mw700">
              {this.renderLinkInput(
                this.store.stxLinkInputValue,
                Localization.translator.shareToExternalProject,
                () => this.store.copyToClipboardSTX(),
                <ArrowUpRightIcon size={14} className="tw:shrink-0" />,
                Localization.translator.generateShareableLink,
                true,
              )}
              <Separator className="tw:my-5" />
              {this.renderLinkInput(
                this.store.inputValue,
                Localization.translator.shareIcalCalendarLink,
                this.store.copyToClipboard,
                <CalendarIcon size={14} className="tw:shrink-0" />,
                Localization.translator.generateShareableCalendarLink,
                true,
              )}
              <Separator className="tw:mt-5" />
              {this.renderSharingSettings()}
            </div>
            <div className="col ba-light-input-border pa20 brada10 mw700 ml20">
              {this.renderImportInput()}
            </div>
          </div>
        </div>
      </>
    )
  }

  private renderSharingSettings(): JSX.Element {
    const {
      calendarFormSharingMode,
      calendarDeliverySharingMode,
      isEnabled,
      changeDeliverySharingDateRange,
      deliverySharingTo,
      deliverySharingFrom,
      formSharingFrom,
      formSharingTo,
      changeFormSharingDateRange,
      currentFormSharingEndOption,
      currentDeliverySharingEndOption,
    } = this.store

    const shouldRenderDeliveryDatePickers =
      calendarDeliverySharingMode === SharingMode.AllInDateRange && isEnabled
    const shouldRenderFormDatePickers =
      calendarFormSharingMode === SharingMode.AllInDateRange && isEnabled

    return (
      <div className="col pa10">
        {this.renderSectionHeader(
          Localization.translator.sharingSettings,
          <Settings2Icon size={14} className="tw:shrink-0" />,
          null,
          true,
        )}
        {this.renderFormSharingOptions()}
        {shouldRenderFormDatePickers && (
          <CalendarDatePicker
            store={this.store}
            openFrom={ExternalCalendarDateRange.FORM_FROM}
            openTo={ExternalCalendarDateRange.FORM_TO}
            from={formSharingFrom}
            to={formSharingTo}
            handler={changeFormSharingDateRange}
            endOption={currentFormSharingEndOption}
          />
        )}
        {this.renderDeliverySharingOptions()}
        {shouldRenderDeliveryDatePickers && (
          <CalendarDatePicker
            store={this.store}
            openFrom={ExternalCalendarDateRange.DELIVERY_FROM}
            openTo={ExternalCalendarDateRange.DELIVERY_TO}
            from={deliverySharingFrom}
            to={deliverySharingTo}
            handler={changeDeliverySharingDateRange}
            endOption={currentDeliverySharingEndOption}
          />
        )}
      </div>
    )
  }

  private renderSectionHeader(
    headerText: string,
    icon: JSX.Element,
    toolTipText: string,
    shouldHideTooltip: boolean = false,
  ): JSX.Element {
    return (
      <div className="row mb20">
        {icon}
        <span className="ml10 text bold uppercase nowrap no-grow">
          {headerText}
        </span>
        {!shouldHideTooltip && (
          <TooltipWrapper content={toolTipText} className="row no-grow ml4">
            <Info size={14} className="tw:block tw:text-muted-foreground" />
          </TooltipWrapper>
        )}
      </div>
    )
  }

  private renderLinkInput(
    link: string,
    headerText: string,
    copyToClipboard: () => void,
    icon: JSX.Element,
    toolTipText: string,
    shouldRenderToggle: boolean = false,
  ): JSX.Element {
    return (
      <div className="col pa10">
        {this.renderSectionHeader(headerText, icon, toolTipText)}
        <div className="tw:flex tw:items-center ml20">
          <StruxhubInput
            value={link}
            isReadOnly={true}
            isDisabled={!this.store.isEnabled}
            className="tw:grow mw380 ma0"
            isRequiredTextHidden
            isMinimalisticMode
          />
          <Button
            variant="primary-outline"
            className="tw:ml-2.5 tw:mb-1"
            onClick={copyToClipboard}
          >
            <Copy data-icon="inline-start" />
            {Localization.translator.copyLink}
          </Button>
          {shouldRenderToggle && this.renderCalendarSwitch()}
        </div>
      </div>
    )
  }

  private renderImportInput(): JSX.Element {
    const {
      isLoading,
      importInputValue,
      setImportInputValue,
      subscribe,
      importInputError,
      isImportUrlValid,
    } = this.store

    return (
      <>
        {this.renderSectionHeader(
          Localization.translator.subscribeToExternalProjects,
          <ArrowDownLeftIcon size={14} className="tw:shrink-0" />,
          Localization.translator.subscribeShareableLink,
        )}
        <div className="tw:flex tw:items-start setup-form-parameter px30">
          <StruxhubInput
            isLabelHidden
            placeholder={Localization.translator.pastUrlFromOtherProject}
            className="tw:grow w200 ma0"
            withDelay
            inputDelay={INPUT_DELAY}
            onChange={setImportInputValue}
            value={importInputValue}
            isValid={!importInputError}
            validationMessage={importInputError}
          />
          <Button
            variant="secondary"
            className="tw:ml-2.5 tw:mt-1"
            onClick={subscribe.bind(null, importInputValue)}
            disabled={isLoading || !isImportUrlValid}
          >
            {isLoading ? (
              <Spinner size="xs" />
            ) : (
              <>
                <Import data-icon="inline-start" />
                {Localization.translator.import}
              </>
            )}
          </Button>
        </div>
        <CalendarSubscriptions store={this.store} />
      </>
    )
  }

  private renderCalendarSwitch(): JSX.Element {
    const { isEnabled, isSwitchDisabled, toggleDisableAccessDialog } =
      this.store

    return (
      <div className="tw:mb-1 tw:flex tw:items-center tw:gap-1.5 tw:ml-2.5">
        <TooltipWrapper
          content={
            <div className="tw:flex tw:flex-col tw:gap-2">
              <span>{Localization.translator.ifTheProjectIsEnabled}</span>
              <span>{Localization.translator.ifTheProjectIsDisabled}</span>
            </div>
          }
          className="tw:flex tw:items-center"
        >
          <Info size={18} className="tw:block tw:text-muted-foreground" />
        </TooltipWrapper>
        <span className="text">
          {isEnabled
            ? Localization.translator.enabled
            : Localization.translator.disabled}
        </span>
        <Switch
          checked={isEnabled}
          onCheckedChange={toggleDisableAccessDialog}
          disabled={isSwitchDisabled}
        />
      </div>
    )
  }

  private renderDeliverySharingOptions(): JSX.Element {
    const { deliverySharingFrom, deliverySharingTo } = this.store

    const parsedFrom = new Date(deliverySharingFrom)
    const parsedTo = new Date(deliverySharingTo)

    if (!isValid(parsedFrom) || !isValid(parsedTo)) {
      return null
    }

    return (
      <CalendarOptionsPicker
        store={this.store}
        workflowType={ExternalCalendarWorkflowType.DELIVERY}
        fromRulesLabelText={Localization.translator.deliveryNotifications}
        allInDateRangeLabelText={
          Localization.translator.shareAllDeliveriesWithinSpecificDate
        }
      />
    )
  }

  private renderFormSharingOptions(): JSX.Element {
    const { formSharingFrom, formSharingTo } = this.store

    const parsedFrom = new Date(formSharingFrom)
    const parsedTo = new Date(formSharingTo)

    if (!isValid(parsedFrom) || !isValid(parsedTo)) {
      return null
    }

    return (
      <CalendarOptionsPicker
        store={this.store}
        workflowType={ExternalCalendarWorkflowType.FORM}
        fromRulesLabelText={Localization.translator.formNotifications}
        allInDateRangeLabelText={
          Localization.translator.shareAllFormsWithinSpecificDate
        }
      />
    )
  }

  private renderRemoveSubscriptionDialog(): JSX.Element {
    return (
      <div className="fixed full-height full-width disable-ical-dialog row x-center y-center">
        <div className="ical-dialog-background" />
        <MenuCloser
          closeMenu={this.store.setIsRemoveSubscriptionDialogShown.bind(
            this,
            false,
            null,
          )}
        >
          <div className="col bg-white no-grow brada10 mw590 ical-dialog-content">
            <div className="text bold size22 mb10">
              {Localization.translator.areYouSureYouWantToDeleteSubscription}
            </div>
            <div className="text large primary mb10">
              {
                Localization.translator
                  .inThisCaseYouWillNotBeAbleToSeeAnyWorkflowsFromThisProject
              }
            </div>
            <div className="row x-end">
              <div
                onClick={this.store.setIsRemoveSubscriptionDialogShown.bind(
                  this,
                  false,
                  null,
                )}
                className="no-grow ical-close-button pa10 ba-none pointer text large medium-bold"
              >
                {Localization.translator.cancel}
              </div>
              <div
                className="disable-ical-button medium-bold text large py10 px20 brada24 no-grow text nowrap ical-confirm-button-long pointer ml10"
                onClick={this.store.unsubscribe.bind(
                  null,
                  this.store.subscriptionIdToRemove,
                )}
              >
                {Localization.translator.deleteSubscription}
              </div>
            </div>
          </div>
        </MenuCloser>
      </div>
    )
  }

  private renderCloseDialog(): JSX.Element {
    return (
      <div className="fixed full-height full-width disable-ical-dialog row x-center y-center">
        <div className="ical-dialog-background" />
        <MenuCloser
          closeMenu={this.store.setIsDisableAccessDialogShown.bind(
            this,
            false,
            false,
          )}
        >
          <div className="col bg-white no-grow brada10 mw590 ical-dialog-content">
            <div className="text bold size22 mb10">
              {Localization.translator.areYouSureYouWantToDisableAccess}
            </div>
            <div className="text large primary mb10">
              {Localization.translator.allExternalProjectsWillLosePermission}
            </div>
            <div className="row x-end">
              <div
                onClick={this.store.setIsDisableAccessDialogShown.bind(
                  this,
                  false,
                  false,
                )}
                className="no-grow ical-close-button pa10 ba-none pointer text large medium-bold"
              >
                {Localization.translator.cancel}
              </div>
              <div
                className="disable-ical-button medium-bold text large py10 px20 brada24 no-grow text nowrap ical-confirm-button pointer ml10"
                onClick={this.store.setIsDisableAccessDialogShown.bind(
                  this,
                  false,
                  true,
                )}
              >
                {Localization.translator.disableAccess}
              </div>
            </div>
          </div>
        </MenuCloser>
      </div>
    )
  }

  private get store(): CalendarLinkInputStore {
    return this.props.store
  }
}
